import '@nectary/components/inline-alert'
import '@nectary/components/skeleton'
import '@nectary/components/skeleton-item'
import { WidgetThemeProvider } from '@sinch/livechat/lib/provider/WidgetThemeProvider.tsx'
import { useLoadLanguage } from 'common-react/src/hooks/useLoadLanguage.ts'
import { useEffect } from 'react'
import styled from 'styled-components'
import { V1Channels, V1MultiChannelWidget } from '../../generated/widget'
import { createLazyLoaderForLanguage } from '../../i18n/languages.ts'
import { ForwardingChannel, ForwardingPageContent } from '../ForwardingPageContent.tsx'
import { ContainerSkeleton } from './ContainerSkeleton.tsx'

const Root = styled.div`
  width: calc(100vw - 68px);
  height: 100%;
  display: block;
  padding: 24px;
`

/**
 * Container - Standalone page wrapper for the forwarding page.
 * Handles language loading, URL param parsing, and document title.
 * Delegates the actual UI to the shared ForwardingPageContent component.
 */
export const Container = ({ data }: { data: V1MultiChannelWidget }) => {
  const searchParams = new URLSearchParams(window.location.search)
  const lang = (searchParams.get('lang') || 'en').toLowerCase()

  const isLoadingLanguage = useLoadLanguage(lang, 'forwardingPage', createLazyLoaderForLanguage)

  // Get channel from URL param or fallback to first enabled channel that is not livechat
  let channel = searchParams.get('channel')?.toLowerCase()
  if (!channel) {
    channel =
      (data?.channels &&
        Object.keys(data.channels).find(
          k =>
            k !== 'livechat' &&
            k !== 'growth' &&
            (data.channels?.[k as keyof V1Channels] as { enabled?: boolean })?.enabled,
        )) ||
      ''
  }

  // Set document title based on channel name
  const channelName = channel ? (data?.channels?.[channel as keyof V1Channels] as any)?.name || '' : ''
  useEffect(() => {
    if (channelName) {
      window.document.title = channelName
    }
  }, [channelName])

  if (!data || isLoadingLanguage || !channel) {
    return (
      <Root>
        <ContainerSkeleton />
      </Root>
    )
  }

  return (
    <Root>
      <WidgetThemeProvider>
        <ForwardingPageContent widget={data} channel={channel as ForwardingChannel} />
      </WidgetThemeProvider>
    </Root>
  )
}
